import {
  Dispatch,
  SetStateAction,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

const noop = () => {};
const isBrowser = typeof window !== 'undefined';

const getSerializer = <T>(options: parserOptions<T> | undefined) => {
  if (!options) return JSON.stringify;
  if (options.raw) return String;
  return options.serializer;
};

const getDeserializer = <T>(options: parserOptions<T> | undefined) => {
  if (!options) return JSON.parse;
  if (options.raw) return (value: string) => value;
  return options.deserializer;
};

type parserOptions<T> =
  | {
      raw: true;
    }
  | {
      raw: false;
      serializer: (value: T) => string;
      deserializer: (value: string) => T;
    };

export const useLocalStorage = <T>(
  key: string,
  initialValue?: T,
  options?: parserOptions<T>
): [T | undefined, Dispatch<SetStateAction<T | undefined>>, () => void] => {
  if (!isBrowser) {
    return [initialValue, noop, noop];
  }
  if (!key) {
    throw new Error('useLocalStorage key may not be falsy');
  }

  const deserializer = getDeserializer(options);

  const initializer = useRef((key: string) => {
    try {
      const serializer = getSerializer(options);

      const localStorageValue = localStorage.getItem(key);
      if (localStorageValue !== null) {
        return deserializer(localStorageValue);
      } else {
        if (initialValue) {
          localStorage.setItem(key, serializer(initialValue));
        }
        return initialValue;
      }
    } catch {
      // If user is in private mode or has storage restriction
      // localStorage can throw. JSON.parse and JSON.stringify
      // can throw, too.
      return initialValue;
    }
  });

  const [state, setState] = useState<T | undefined>(() =>
    initializer.current(key)
  );

  useLayoutEffect(() => setState(initializer.current(key)), [key]);

  const set: Dispatch<SetStateAction<T | undefined>> = useCallback(
    (valOrFunc) => {
      setState((prevState) => {
        try {
          const newState =
            typeof valOrFunc === 'function'
              ? (valOrFunc as (prev: T | undefined) => T | undefined)(prevState)
              : valOrFunc;

          if (typeof newState === 'undefined') return prevState;

          let value: string;
          const serializer = getSerializer(options);

          value = serializer(newState);
          localStorage.setItem(key, value);

          return newState;
        } catch {
          // If user is in private mode or has storage restriction
          return prevState;
        }
      });
    },
    [key, options]
  );

  const remove = useCallback(() => {
    try {
      localStorage.removeItem(key);
      setState(undefined);
    } catch {
      // If user is in private mode or has storage restriction
      // localStorage can throw.
    }
  }, [key, setState]);

  return [state, set, remove];
};
